import { SectionBox } from '@shared/components/ui/SectionBox';
import { faArrowRight, faCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentCorrections } from '../../hooks/usePaymentCorrections';
import { usePaymentMethods } from '../../hooks';

export const PaymentCorrections: FC = () => {
  const { t } = useTranslation('viewOrdersModule');
  const { data: corrections, isLoading } = usePaymentCorrections();
  const { paymentMethods } = usePaymentMethods();

  const methodMap = useMemo(() => new Map(paymentMethods.map((m) => [m.id, m.name])), [paymentMethods]);

  if (isLoading || !corrections || corrections.length === 0) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const useTimeline = corrections.length > 1;

  const renderContent = (correction: (typeof corrections)[0], index: number) => {
    const isDecrease = correction.adjustment_type === 'TOTAL_DECREASE';
    const isLast = index === corrections.length - 1;
    const amountChanged = correction.before_snapshot.total !== correction.after_snapshot.total;
    const methodChanged = correction.before_snapshot.payment_method_id !== correction.after_snapshot.payment_method_id;
    const beforeMethod = methodMap.get(correction.before_snapshot.payment_method_id);
    const afterMethod = methodMap.get(correction.after_snapshot.payment_method_id);

    const body = (
      <>
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex flex-wrap gap-1">
            {amountChanged && (
              <span
                className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  isDecrease ? 'bg-destructive text-white' : 'bg-bg-success text-success'
                }`}
              >
                {isDecrease
                  ? `${t('paymentCorrections.type.decrease')} -${correction.adjustment_amount.toFixed(2)}`
                  : `${t('paymentCorrections.type.increase')} +${correction.adjustment_amount.toFixed(2)}`}
              </span>
            )}
            {methodChanged && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-bg-info text-info">
                {t('paymentCorrections.type.methodChange')}
              </span>
            )}
            {!amountChanged && !methodChanged && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                {t('paymentCorrections.type.correction')}
              </span>
            )}
          </div>
          <span className="text-xs text-complementary-text shrink-0">{formatDate(correction.created_at)}</span>
        </div>
        {amountChanged && (
          <div className="flex items-center gap-1.5 text-sm mt-1">
            <span className="text-complementary-text">{correction.before_snapshot.total.toFixed(2)}</span>
            <FontAwesomeIcon icon={faArrowRight} className="!w-3 !h-3 text-complementary-text" />
            <span className="font-medium">{correction.after_snapshot.total.toFixed(2)}</span>
          </div>
        )}
        {methodChanged && (beforeMethod || afterMethod) && (
          <div className="flex items-center gap-1.5 text-xs text-complementary-text mt-0.5">
            <span>{beforeMethod ?? '—'}</span>
            <FontAwesomeIcon icon={faArrowRight} className="!w-2.5 !h-2.5" />
            <span className="text-text font-medium">{afterMethod ?? '—'}</span>
          </div>
        )}
        <p className="text-xs text-complementary-text mt-1 line-clamp-2">
          <span className="font-medium">{t('paymentCorrections.reason')}: </span>
          <span className="text-text">{correction.reason}</span>
        </p>
        {useTimeline && !isLast && <div className="mt-3 mb-3 border-t border-card-border" />}
      </>
    );

    if (!useTimeline) {
      return <div key={correction.id}>{body}</div>;
    }

    return (
      <div
        key={correction.id}
        className={`flex items-start gap-4 relative !mt-0 ${isLast ? 'bg-card-bg' : 'bg-transparent'}`}
      >
        {/* Punto */}
        <div className="relative z-10 flex items-start border-[5px] border-card-bg rounded-full ml-[-2px]">
          <FontAwesomeIcon icon={faCircle} className="!w-3 !h-3 text-subtext bg-card-bg" />
        </div>
        {/* Contenido */}
        <div className="flex-1 min-w-0 pb-1">{body}</div>
      </div>
    );
  };

  return (
    <SectionBox className="bg-card-bg">
      <h2 className="text-lg font-bold mb-5">{t('paymentCorrections.title')}</h2>

      {useTimeline ? (
        <div className="relative">
          {/* Línea vertical */}
          <div className="absolute left-2 top-1 bottom-0 w-px bg-lines" />
          <div className="space-y-4">{corrections.map((c, i) => renderContent(c, i))}</div>
        </div>
      ) : (
        <div>{corrections.map((c, i) => renderContent(c, i))}</div>
      )}
    </SectionBox>
  );
};
